/**
 * Digital product utility functions for managing terms and conditions
 */

// Type definitions for CGU (Conditions Générales d'Utilisation) options
export type AudienceOption = 'all' | 'new';
export type UsageOption = 'one' | 'many';
export type BuyOption = 'buy_many' | 'buy_one';
export type PerOrderOption = 'one_per_order' | 'many_per_order';
export type CumulOption = 'non_cumulable' | 'cumulable';
export type SubOption = 'no_subscription' | 'subscription';
export type ShippingOption = 'shipping_paid' | 'shipping_free';

export interface CguConfig {
  audience: {
    include: boolean;
    option: AudienceOption | undefined;
  };
  usage: {
    include: boolean;
    option: UsageOption | undefined;
  };
  buy: {
    include: boolean;
    option: BuyOption | undefined;
  };
  perOrder: {
    include: boolean;
    option: PerOrderOption | undefined;
  };
  cumul: {
    include: boolean;
    option: CumulOption | undefined;
  };
  subscription: {
    include: boolean;
    option: SubOption | undefined;
  };
  shipping: {
    include: boolean;
    option: ShippingOption | undefined;
  };
  extra: string;
}

export interface BuildCouponTermsOptions {
  cgu: CguConfig;
  experienceDeadline?: string;
}

/**
 * Builds an array of coupon terms based on CGU configuration
 * @param options Configuration object containing CGU settings and optional experience deadline
 * @returns Array of formatted terms as strings
 */
export function buildCouponTerms(options: BuildCouponTermsOptions): string[] {
  const { cgu, experienceDeadline } = options;
  const lines: string[] = [];

  // Add experience deadline as first line if provided
  if (experienceDeadline) {
    const date = new Date(experienceDeadline);
    const formattedDate = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    lines.push(
      `Date limite pour réaliser l'expérience : ${formattedDate} inclus.`
    );
  }

  // Audience terms
  if (cgu.audience.include && cgu.audience.option) {
    lines.push(
      `Valable pour ${
        cgu.audience.option === 'all'
          ? 'tous les clients'
          : 'les nouveaux clients uniquement'
      }.`
    );
  }

  // Usage terms
  if (cgu.usage.include && cgu.usage.option) {
    lines.push(
      `Utilisable ${
        cgu.usage.option === 'one'
          ? 'uniquement en 1 seule fois'
          : 'plusieurs fois'
      } par client.`
    );
  }

  // Buy terms
  if (cgu.buy.include && cgu.buy.option) {
    lines.push(
      `Le client peut acheter ${
        cgu.buy.option === 'buy_many' ? 'plusieurs codes' : 'un seul code'
      }.`
    );
  }

  // Per order terms
  if (cgu.perOrder.include && cgu.perOrder.option) {
    lines.push(
      `${
        cgu.perOrder.option === 'one_per_order'
          ? 'Un code = une commande'
          : 'Plusieurs codes utilisables dans la même commande'
      } sur le site de la marque.`
    );
  }

  // Cumul terms
  if (cgu.cumul.include && cgu.cumul.option) {
    lines.push(
      `Offre ${
        cgu.cumul.option === 'non_cumulable' ? 'non cumulable' : 'cumulable'
      } avec d'autres offres en cours sur le site de la marque.`
    );
  }

  // Subscription terms
  if (cgu.subscription.include && cgu.subscription.option) {
    lines.push(
      `Cet achat ${
        cgu.subscription.option === 'no_subscription'
          ? 'ne donne pas suite à un abonnement'
          : 'donne suite à un abonnement avec engagement'
      } sur le site de la marque.`
    );
  }

  // Shipping terms
  if (cgu.shipping.include && cgu.shipping.option) {
    lines.push(
      `${
        cgu.shipping.option === 'shipping_free'
          ? 'Aucun frais de port ne seront à régler'
          : 'Des frais de port seront à régler'
      } sur le site de la marque.`
    );
  }

  // Extra terms
  if (cgu.extra.trim()) {
    lines.push(cgu.extra.trim());
  }

  return lines;
}
